// utils/comisionEdificiosUtil.js

/**
 * Resuelve la mejor fecha disponible para determinar el período de comisión.
 * Prioridad: facturafecha > mes_cierre (MM-YYYY) > mes+ano > hoy
 */
export const resolverFechaFactura = (factura) => {
  if (factura.facturafecha) return factura.facturafecha;
  if (factura.mes_cierre) {
    const [mes, ano] = factura.mes_cierre.split("-");
    return `${ano}-${mes}-01`;
  }
  if (factura.ano && factura.mes) {
    return `${factura.ano}-${String(factura.mes).padStart(2, "0")}-01`;
  }
  return new Date().toISOString();
};

/**
 * Calcula el porcentaje de comisión del edificio según la fecha de factura.
 * Prioridad: rango 1 > rango 2 > rango 3.
 * Si la fecha no cae en ningún rango definido, retorna 0 (configuración vencida
 * o no aplicable). Antes había un fallback silencioso a comision_3_porcentaje
 * que ocultaba contratos vencidos y producía cobros fantasma.
 */
export const calcularPorcentajeComisionEdificio = (edificio, fechaFacturaStr) => {
  if (!edificio || !fechaFacturaStr) return 0;

  const fechaFactura = new Date(fechaFacturaStr);
  if (isNaN(fechaFactura.getTime())) return 0;

  const rangos = [
    {
      inicio: edificio.comision_1_fechainicio,
      fin: edificio.comision_1_fechafin,
      porcentaje: edificio.comision_1_porcentaje || 0,
    },
    {
      inicio: edificio.comision_2_fechainicio,
      fin: edificio.comision_2_fechafin,
      porcentaje: edificio.comision_2_porcentaje || 0,
    },
    {
      inicio: edificio.comision_3_fechainicio,
      fin: edificio.comision_3_fechafin,
      porcentaje: edificio.comision_3_porcentaje || 0,
    },
  ];

  for (const rango of rangos) {
    if (rango.inicio && rango.fin) {
      const inicio = new Date(rango.inicio);
      const fin = new Date(rango.fin);
      if (fechaFactura >= inicio && fechaFactura <= fin) {
        return rango.porcentaje;
      }
    }
  }

  return 0;
};

/**
 * Desglosa las pantallas ajustadas de un edificio en una factura/venta POR CIRCUITO,
 * sin sumar. Fuente única de cómo se cuentan los circuitos por edificio.
 * `csvs` = objeto con pantallas_ascensores/circulacion/espera/cowork (CSV de IDs de edificio).
 * Mapeo circuito → campo del edificio: asc→pantallas, circ→totems, esp→espera, cow→cowork.
 */
export const getDesglosePorEdificio = (edificio, csvs) => {
  const { pantallas_ascensores, pantallas_espera, pantallas_circulacion, pantallas_cowork } = csvs || {};
  const edificioId = String(edificio.id);

  const camposVacios = !pantallas_ascensores && !pantallas_espera && !pantallas_circulacion && !pantallas_cowork;

  if (camposVacios) {
    // Sin CSV de circuitos → cuentan los 4 circuitos del edificio (mismo fallback que antes)
    return {
      pantallas_asc: edificio.pantallas || 0,
      pantallas_circ: edificio.totems || 0,
      pantallas_esp: edificio.espera || 0,
      pantallas_cow: edificio.cowork || 0,
    };
  }

  return {
    pantallas_asc: pantallas_ascensores?.split(",").includes(edificioId) ? edificio.pantallas || 0 : 0,
    pantallas_circ: pantallas_circulacion?.split(",").includes(edificioId) ? edificio.totems || 0 : 0,
    pantallas_esp: pantallas_espera?.split(",").includes(edificioId) ? edificio.espera || 0 : 0,
    pantallas_cow: pantallas_cowork?.split(",").includes(edificioId) ? edificio.cowork || 0 : 0,
  };
};

/**
 * Resuelve, para UN mes de contrato (`mesObj` del jsonb `meses`), los 4 CSV de circuitos
 * (asc/circ/esp/cow) y el CSV de edificios participantes — con la MISMA lógica que el motor vivo
 * (`explodarContratosComoFacturas`). Es la FUENTE ÚNICA de esa resolución (regla 10.1): la consumen
 * el exploder (cálculo vivo) Y la captura del snapshot (`capturarSnapshotDesdeContrato`), de modo que
 * el denominador CONGELADO de la foto sea idéntico al VIVO. Antes divergían: la captura resolvía solo
 * por `edificios_ids` y, cuando venía NULL, salía una foto vacía mientras el vivo sí leía `pantallas_*`
 * (bug real: sales_contratos 11 → 31 edificios en $0).
 *
 * Prioridad: si el mes trae algún `pantallas_*`, se usan tal cual. Si los 4 están vacíos pero hay
 * `edificios_ids`, se derivan asignando cada edificio a los circuitos que tenga (>0). Participantes =
 * `edificios_ids` si viene; si no, la unión de los 4 CSV. `vacio=true` cuando el mes no tiene NINGÚN
 * edificio (ni pantallas_* ni edificios_ids) → el caller no debe capturar foto (correcto).
 */
export const resolverCircuitosParticipantesMes = (mesObj = {}, edificiosMap = {}) => {
  let pAsc = mesObj.pantallas_ascensores || "";
  let pEsp = mesObj.pantallas_espera || "";
  let pCir = mesObj.pantallas_circulacion || "";
  let pCow = mesObj.pantallas_cowork || "";
  if (!pAsc && !pEsp && !pCir && !pCow && mesObj.edificios_ids) {
    const ids = mesObj.edificios_ids
      .split(",")
      .map((s) => Number(s.trim()))
      .filter(Boolean);
    const asc = [];
    const esp = [];
    const cir = [];
    const cow = [];
    ids.forEach((eid) => {
      const e = edificiosMap[eid];
      if (!e) return;
      if ((Number(e.pantallas) || 0) > 0) asc.push(eid);
      if ((Number(e.espera) || 0) > 0) esp.push(eid);
      if ((Number(e.totems) || 0) > 0) cir.push(eid);
      if ((Number(e.cowork) || 0) > 0) cow.push(eid);
    });
    pAsc = asc.join(",");
    pEsp = esp.join(",");
    pCir = cir.join(",");
    pCow = cow.join(",");
  }
  const vacio = !pAsc && !pEsp && !pCir && !pCow;
  const edificiosParticipantes =
    mesObj.edificios_ids ||
    [
      ...new Set(
        [pAsc, pCir, pEsp, pCow]
          .filter(Boolean)
          .flatMap((s) => s.split(","))
          .map((s) => s.trim())
          .filter(Boolean)
      ),
    ].join(",");
  return { pAsc, pEsp, pCir, pCow, edificiosParticipantes, vacio };
};

/**
 * Calcula pantallas ajustadas del edificio en una factura (pauta).
 * Suma el desglose por circuito (getDesglosePorEdificio) → una sola fuente de conteo.
 */
export const calcularPantallasAjustadas = (edificio, factura) => {
  const d = getDesglosePorEdificio(edificio, factura);
  return d.pantallas_asc + d.pantallas_circ + d.pantallas_esp + d.pantallas_cow;
};

/**
 * Deriva la clave del snapshot de comisión desde una factura del motor.
 * - Venta puntual: `factura.id` numérico → 's:{sale_id}'.
 * - Contrato-mes: `factura.id` = "contrato-{contrato_id}-{mes}" (mes "MM-YYYY") → 'c:{contrato_id}|{mes}'.
 * - BTL (`isBTL`): no usa denominador → null.
 */
export const keyDeFactura = (factura) => {
  if (!factura || factura.isBTL) return null;
  if (factura.isContrato || (typeof factura.id === "string" && factura.id.startsWith("contrato-"))) {
    const m = String(factura.id).match(/^contrato-(\d+)-(\d{2}-\d{4})$/);
    return m ? `c:${m[1]}|${m[2]}` : null;
  }
  return `s:${factura.id}`;
};

/**
 * Denominador de la proporción de comisión. Wrapper DELGADO de `resolverDesgloseComision`
 * (única fuente de verdad): devuelve su `.denominador`. Con foto → `denominador_total` congelado;
 * sin foto → `factura.pantallastotales`. `snapMap` = Map de `cargarSnapshotComision`
 * (forma anidada `{ denominador_total, porEdificio }`; ver comisionSnapshot.js). Pasa `edificio`
 * null porque acá solo interesa el denominador (el resolver lo tolera).
 */
export const getDenominadorComision = (factura, snapMap = null) =>
  resolverDesgloseComision(factura, null, snapMap).denominador;

// ── Traza de fallback por foto de comisión DEGENERADA (regla 10.177 — prohibido el fallo silencioso) ──
// Marcador grep-able `[SNAPSHOT_TRACE]` (el MISMO que usa `comisionSnapshot.traceSnapshot`, para que la
// red de seguridad existente lo capture). Se emite acá INLINE a propósito: no acoplar este util puro de
// cálculo con el módulo de IO de snapshots evita una dependencia circular (comisionSnapshot ya importa
// `getDesglosePorEdificio` de este archivo). Se deduplica por `key`: el resolver corre por (factura ×
// edificio), así que una misma foto rota se visita N veces por render → se avisa UNA sola vez por
// documento. Nunca lanza (la traza jamás debe romper el cálculo que la emite).
const _fotosDegeneradasAvisadas = new Set();
const avisarFotoDegenerada = (key, foto) => {
  try {
    if (!key || _fotosDegeneradasAvisadas.has(key)) return;
    _fotosDegeneradasAvisadas.add(key);
    // eslint-disable-next-line no-console
    console.error(
      "[SNAPSHOT_TRACE]",
      JSON.stringify({
        evento: "foto_degenerada_fallback_vivo",
        key,
        denominador_total: foto?.denominador_total ?? null,
        hijos: foto?.porEdificio?.size ?? 0,
      })
    );
  } catch {
    /* noop: la traza jamás debe romper el cálculo que la emite */
  }
};

/**
 * Resolver ÚNICO del desglose de comisión de una venta+edificio. Devuelve numerador, denominador,
 * circuitos y fuente — TODO resuelto en UNA sola rama (invariante de fuente única): o todo del
 * snapshot congelado, o todo en vivo. NUNCA mezcla congelado con vivo (esa mezcla —numerador vivo
 * ÷ denominador congelado— ES el bug del blindaje que este resolver viene a cerrar de raíz).
 *
 * `snapMap` = Map de `cargarSnapshotComision` (comisionSnapshot.js):
 *   Map<key, { denominador_total, porEdificio: Map<edificioId, {asc,circ,esp,cow}> }>.
 * - Hay foto para esa venta → fuente "snapshot": numerador = suma de los 4 circuitos del hijo de
 *   ese edificio; denominador = denominador_total; circuitos = el desglose congelado.
 * - No hay foto → fuente "vivo": numerador vía getDesglosePorEdificio (ficha viva) y denominador =
 *   factura.pantallastotales — ambos ejes vivos, coherentes entre sí.
 * - Foto DEGENERADA (existe pero denominador_total <= 0 / null, o sin ningún hijo) → se ignora la
 *   foto y se resuelve VIVO (fuente "vivo_foto_degenerada") + traza `[SNAPSHOT_TRACE]` (regla 10.177):
 *   evita el $0 silencioso que daría numerador/0. Una foto VÁLIDA con un edificio que no participa
 *   (numerador 0, denominador > 0) NO es degenerada y sigue devolviendo 0 para ese edificio.
 *
 * @returns {{numerador:number, denominador:number, circuitos:{asc,circ,esp,cow}, fuente:string}}
 */
export const resolverDesgloseComision = (factura, edificio, snapMap = null) => {
  const key = keyDeFactura(factura);
  const foto = key && snapMap ? snapMap.get(key) : undefined;

  // Una foto es DEGENERADA (inservible) si EXISTE pero su denominador es <= 0 / null, o NO tiene
  // ningún edificio hijo. Con foto degenerada la rama snapshot devolvería denominador 0 y, aguas
  // abajo, proporcion = numerador/0 = 0 → comisión $0 EN SILENCIO para TODOS los edificios de la
  // factura (deuda real: sales_contratos 11, fotos del backfill 07-11 con denominador_total=0 y 0
  // hijos → 31 edificios en $0). Se trata como si NO hubiera foto y se cae a la rama VIVO (idéntico
  // cálculo al de `foto` undefined), con traza visible (regla 10.177: nunca en silencio).
  // OJO: NO es degenerada una foto VÁLIDA donde un edificio puntual no participa (numerador 0 con
  // denominador > 0) — esa sigue devolviendo 0 para ESE edificio, como hoy (no entra acá).
  const fotoDegenerada = !!foto && (!(foto.denominador_total > 0) || !foto.porEdificio || foto.porEdificio.size === 0);

  if (foto && !fotoDegenerada) {
    // FUENTE = SNAPSHOT: numerador, denominador y circuitos, todos de la MISMA foto congelada.
    const h = foto.porEdificio.get(Number(edificio?.id)) || { asc: 0, circ: 0, esp: 0, cow: 0 };
    return {
      numerador: h.asc + h.circ + h.esp + h.cow,
      denominador: foto.denominador_total ?? 0,
      circuitos: { asc: h.asc, circ: h.circ, esp: h.esp, cow: h.cow },
      fuente: "snapshot",
    };
  }

  // Foto presente pero inservible → aviso (deduplicado por documento) y seguimos a VIVO.
  if (fotoDegenerada) avisarFotoDegenerada(key, foto);

  // FUENTE = VIVO: sin foto utilizable → numerador y denominador ambos de la ficha viva / la venta.
  // `edificio` puede venir null cuando solo se pide el denominador (getDenominadorComision):
  // en ese caso el numerador/circuitos quedan en 0 y el denominador sale de pantallastotales.
  const d = edificio
    ? getDesglosePorEdificio(edificio, factura)
    : { pantallas_asc: 0, pantallas_circ: 0, pantallas_esp: 0, pantallas_cow: 0 };
  return {
    numerador: d.pantallas_asc + d.pantallas_circ + d.pantallas_esp + d.pantallas_cow,
    denominador: factura?.pantallastotales ?? 0,
    circuitos: { asc: d.pantallas_asc, circ: d.pantallas_circ, esp: d.pantallas_esp, cow: d.pantallas_cow },
    // Etiqueta distinta cuando el vivo viene de un fallback por foto rota (rastro adicional; ningún
    // consumidor ramifica por `fuente`, así que no hay regresión). Sin foto → "vivo" como siempre.
    fuente: fotoDegenerada ? "vivo_foto_degenerada" : "vivo",
  };
};

/**
 * Comisión edificios para factura PAUTA (multi-edificio).
 * `snapMap` OPCIONAL: si es null → todo vivo (idéntico a pre-blindaje); si viene → todo del snapshot.
 * BLINDAJE (Fase 1): numerador Y denominador salen de la MISMA foto vía `resolverDesgloseComision`
 * — nunca numerador vivo ÷ denominador congelado. Editar la ficha del edificio ya no reescribe la
 * comisión de una venta con foto.
 */
export const calcularComisionEdificioPauta = (factura, edificio, netoFacturaTotal, snapMap = null) => {
  if (esEdificioComisionFija(edificio, resolverFechaFactura(factura))) return 0;
  const { numerador, denominador } = resolverDesgloseComision(factura, edificio, snapMap);
  const proporcion = denominador > 0 ? numerador / denominador : 0;
  const porcentaje = calcularPorcentajeComisionEdificio(edificio, resolverFechaFactura(factura));
  return netoFacturaTotal * proporcion * (porcentaje / 100);
};

/**
 * Función maestra: comisión edificios total para una oportunidad
 */
/**
 * Determina si un edificio tiene comisión fija activa para una fecha dada.
 * Edificios con comisión fija no generan comisión variable mientras la fija esté vigente.
 * Si `comision_fija_fin` está definida, la fija deja de aplicar después de esa fecha
 * (la variable vuelve a habilitarse para fechas posteriores).
 */
export const esEdificioComisionFija = (edificio, fechaStr) => {
  if (!edificio?.comision_fija_monto || !edificio?.comision_fija_inicio || !fechaStr) return false;
  const fecha = new Date(fechaStr);
  const inicio = new Date(edificio.comision_fija_inicio);
  if (isNaN(fecha.getTime()) || isNaN(inicio.getTime()) || fecha < inicio) return false;
  if (edificio.comision_fija_fin) {
    const fin = new Date(edificio.comision_fija_fin);
    if (!isNaN(fin.getTime()) && fecha > fin) return false;
  }
  return true;
};

/**
 * Calcula comisiones fijas mensuales de edificios para un año dado.
 * Para cada mes, busca edificios con comisión fija activa y convierte USD → moneda local
 * usando TC del 1ro del mes.
 *
 * @param {Object} edificiosMap - { [id]: edificioCompleto }
 * @param {number} year - Año a calcular
 * @param {Function} getDolarValueFn - (fechaStr) => number (bound por país)
 * @returns {number[]} Array(12) de comisiones fijas mensuales en moneda local
 */
export const calcularComisionesFijasMensuales = (edificiosMap, year, getDolarValueFn) => {
  const valores = Array(12).fill(0);
  const edificios = Object.values(edificiosMap || {});
  for (let mes = 0; mes < 12; mes++) {
    const fechaPrimero = `${year}-${String(mes + 1).padStart(2, "0")}-01`;
    edificios.forEach((ed) => {
      if (!esEdificioComisionFija(ed, fechaPrimero)) return;
      const monto = Number(ed.comision_fija_monto) || 0;
      if (monto <= 0) return;
      const moneda = (ed.comision_fija_moneda || "USD").toUpperCase();
      valores[mes] += moneda === "USD" ? monto * getDolarValueFn(fechaPrimero) : monto;
    });
  }
  return valores;
};

/**
 * Función maestra: comisión edificios total para una oportunidad.
 * `snapMap` OPCIONAL (Paso 5): se forwardea a `calcularComisionEdificioPauta` para que el
 * prorrateo use el denominador del snapshot; si es null → `pantallastotales` (idéntico a hoy).
 * BTL no prorratea por pantallas → no usa snapMap.
 *
 * `takeLocal` OPCIONAL (FASE 6 — capa aislada, regla 10.48): take de la capa programática
 * (Place/giro) en moneda local, ya convertido por el call site con el rate de la venta
 * (`getTakeLocal`, costosIntermediacion.js). Reduce la base neta UNA sola vez (antes del
 * prorrateo por edificio), de modo que cada edificio prorratea sobre el neto ya descontado el
 * take. Default 0 → idéntico al comportamiento actual (cero regresión). NO aplica a BTL
 * (no prorratea por pantallas; no hay programática BTL).
 */
export const calcularComisionEdificiosTotal = (
  oportunidad,
  edificiosMap,
  netoDespuesAgencias,
  snapMap = null,
  takeLocal = 0,
  apxLocal = 0
) => {
  if (oportunidad.isBTL) {
    // BTL: suma por cada building
    const buildings = oportunidad.buildings || [];
    return buildings.reduce((total, building) => {
      const edificio = edificiosMap[building.id];
      if (!edificio) return total;
      // Regla 10.186: en BTL NO existe comisión fija de edificio — cada BTL se negocia caso a caso,
      // directo con el edificio, y el monto a favor del edificio queda en el jsonb `buildings`. El guard
      // esEdificioComisionFija es un concepto de PAUTA que no aplica a BTL; tenerlo acá divergía con la
      // vista individual (ListadoFacturas, sin guard). Se remueve solo de esta rama; la rama PAUTA (else)
      // y el resto de consumidores de esEdificioComisionFija (fija mensual) no se tocan.
      // Regla 10.186: en BTL la comisión de edificio sale del MONTO hardcodeado del jsonb `buildings`
      // (`building.comision` es el % derivado = monto ÷ importe, con muchos decimales), NUNCA del % de
      // la tabla `edificios` (ese % es solo de PAUTA). Alinea el modo grupo/"Todos" + ledger + ejecutivo
      // con la vista individual (ListadoFacturas) ya corregida. La rama PAUTA (else) no se toca.
      const porcentaje = building.comision || 0;
      const netoBuilding = building.importe;
      return total + netoBuilding * (porcentaje / 100);
    }, 0);
  } else {
    // PAUTA: prorrateo por pantallas. El take (FASE 6) y el APX (Publicis) bajan la base una sola
    // vez acá; cada edificio prorratea sobre el neto ya descontado take + apx.
    const baseNeto = Math.max(0, (netoDespuesAgencias || 0) - (takeLocal || 0) - (apxLocal || 0));
    const edificiosParticipantes = oportunidad.edificiosparticipantes
      ? [...new Set(oportunidad.edificiosparticipantes.split(",").map(Number))]
      : [];
    return edificiosParticipantes.reduce((total, edificioId) => {
      const edificio = edificiosMap[edificioId];
      if (!edificio) return total;
      return total + calcularComisionEdificioPauta(oportunidad, edificio, baseNeto, snapMap);
    }, 0);
  }
};
